import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { FlowerButton } from '@/components/ui/FlowerButton';
import { BudgetBar } from '@/components/common/BudgetBar';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useCampaignSettingsGoogleAdsStore } from '@/store/campaignSettingsGoogleAdsStore';
import { useAccountSettingsStore } from '@/store/accountSettingsStore';
import { CampaignKebabMenu } from './shared/CampaignKebabMenu';
import { PlatformBadge } from './shared/PlatformBadge';
import { usePendingChangesStore } from '@/store/pendingChangesStore';
import { cn } from '@/utils/cn';
import type { Campaign } from '@/types/campaign';
import { formatAccountId, toast } from '@/utils';
import { formatBiddingStrategy, formatCampaignType, formatEnumPretty } from '@/utils/formatEnum';
import { ROAS_CONFIG } from '@/config/constants';
import { computeCampaignRoas, formatRoasDisplay } from '@/utils/roas';

interface CampaignCardProps {
  campaign: Campaign;
  revenuePerClick?: number;
  /** User's ROAS calculation preference: true = conversion-based, false = click-based */
  useConversionBasedRoas?: boolean;
  /** True if ANY campaign in the account has conversions_value > 0 (proves tracking works) */
  conversionTrackingAvailable?: boolean;
  /** True when the user explicitly picked click-based via the toggle
   *  (vs being forced into it by missing conversion data). Drives the
   *  disclaimer copy: user-choice → "Click-based · $X/click", forced →
   *  "No conversion data · $X/click". Without this, Meta and Google
   *  cards diverge whenever one platform happens to have conversion
   *  data and the other doesn't — same user choice, different label. */
  userPickedClickBased?: boolean;
  /** Cross-platform pool total, computed once at the Account-page
   *  level via `useOptimizedCampaigns().totalCurrentBudget` and passed
   *  down so each card doesn't re-run the full cross-store scan on
   *  every render. Used by the pause-math `totalBeforePause`. */
  poolTotalCurrentBudget?: number;
  /** @deprecated No longer used - status changes are now batched via SaveChangesBar */
  onDelete?: (campaignId: string) => void;
  onStatusChange?: (campaignId: string, newStatus: string) => void;
  onOptimisticDelete?: (campaignId: string) => void;
}

export function CampaignCard({
  campaign,
  revenuePerClick,
  useConversionBasedRoas = true,
  conversionTrackingAvailable = false,
  userPickedClickBased = false,
  poolTotalCurrentBudget = 0,
  onStatusChange,
  onOptimisticDelete
}: CampaignCardProps) {
  const navigate = useNavigate();

  // Use selectors to avoid re-rendering when unrelated store state changes
  // Get only this campaign's settings using a memoized selector
  const settings = useCampaignSettingsGoogleAdsStore(
    useCallback((state) => state.campaignSettings.get(campaign.campaign_id), [campaign.campaign_id])
  );
  const totalBudget = useAccountSettingsStore((state) => state.totalBudget);

  // Actions don't cause re-renders but using selectors keeps pattern consistent
  const toggleCampaignOptimization = useCampaignSettingsGoogleAdsStore((state) => state.toggleCampaignOptimization);
  const setManualBudget = useCampaignSettingsGoogleAdsStore((state) => state.setManualBudget);
  const setManualName = useCampaignSettingsGoogleAdsStore((state) => state.setManualName);
  const updateCampaignStatus = useCampaignSettingsGoogleAdsStore((state) => state.updateCampaignStatus);
  const setTotalBudget = useAccountSettingsStore((state) => state.setTotalBudget);
  // Cross-platform pool total comes in via prop from the Account
  // page, which calls `useOptimizedCampaigns()` once and forwards
  // the total to every card. Earlier this card called the hook
  // directly — each MetaCampaignCard + CampaignCard re-ran the full
  // cross-store scan on every render (O(N²)). Lifting the call up
  // is one scan per render shared by N cards. The pause math's
  // reference-frame correctness (cross-platform vs Google-only) is
  // preserved — Account page is the new owner of the computation.
  const totalCurrentBudget = poolTotalCurrentBudget;

  const { queueStatusChange, getPendingStatusChange } = usePendingChangesStore(
    useShallow((state) => ({
      queueStatusChange: state.queueStatusChange,
      getPendingStatusChange: state.getPendingStatusChange,
    }))
  );

  const [editingName, setEditingName] = React.useState(false);
  const [nameValue, setNameValue] = React.useState('');
  const [showMenu, setShowMenu] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showPauseDialog, setShowPauseDialog] = React.useState(false);
  const [showEnableDialog, setShowEnableDialog] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const collapsedMenuRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);

  // Check if there's a pending status change for this campaign
  const pendingChange = getPendingStatusChange('campaign', campaign.campaign_id);

  // Calculate ROAS via the shared `computeCampaignRoas` utility — same
  // logic used by `MetaCampaignCard`. See `src/utils/roas.ts` for the
  // two-tier decision tree (conversion-based when available + opted-in;
  // click-based fallback using `revenue_per_click`).
  const { roas, isUsingClickBasedRoas } = computeCampaignRoas({
    cost: campaign.cost,
    clicks: campaign.clicks,
    conversionsValue: campaign.conversions_value,
    revenuePerClick,
    useConversionBasedRoas,
    conversionTrackingAvailable,
  });
  const shouldUseConversionBased = useConversionBasedRoas && conversionTrackingAvailable;

  const performanceMetrics = [
    { label: 'Clicks', value: campaign.clicks.toLocaleString() },
    { label: 'Impressions', value: campaign.impressions.toLocaleString() },
    { label: 'CTR', value: `${campaign.ctr.toFixed(1)}%` },
    { label: 'CPC', value: `$${campaign.cpc.toFixed(2)}` },
  ];

  const isOptimizationEnabled = settings?.isOptimizationEnabled || false;
  // Source of truth for status forks in this card: prefer the
  // settings-store `campaignStatus`, which reflects pending pause /
  // enable edits immediately, and fall back to the prop's
  // `campaign.status` for the brief pre-init window. The pause/enable
  // handlers and the per-campaign overlay both write through the
  // store, so reading from the prop alone can leave a queued status
  // edit invisible to the budget-fork gates below until the next
  // refetch. Parity with MetaCampaignCard's `cardStatus`.
  const cardStatus = settings?.campaignStatus ?? campaign.status;
  // Budget baseline preference order — parity with MetaCampaignCard:
  //   1. `settings.manualBudget` — user's in-flight staged edit
  //   2. `settings.currentBudget` — store baseline (includes the
  //      post-save promotion done by preSave), so the new value
  //      renders BEFORE the React Query refetch completes
  //   3. `campaign.budget` — live prop fallback for the brief
  //      window before `initializeCampaignSettings` seeds the store
  // `??` (not `||`) throughout — 0 is a valid budget value.
  const settingsBaselineBudget = settings?.currentBudget ?? campaign.budget;
  const currentManualBudget = settings?.manualBudget ?? settingsBaselineBudget;

  const handleOptimizationToggle = (checked: boolean) => {
    toggleCampaignOptimization(campaign.campaign_id, checked);
  };

  const handleResetToCurrentBudget = () => {
    // Reset to the store baseline, not the prop. After save the prop
    // can briefly lag behind the promoted `currentBudget`; resetting
    // against the prop would snap back to the pre-save value.
    setManualBudget(campaign.campaign_id, settingsBaselineBudget);
  };

  const editStartTimeRef = React.useRef<number>(0);

  const handleEditName = () => {
    const currentName = settings?.manualName || campaign.campaign_name;
    setNameValue(currentName);
    editStartTimeRef.current = Date.now();
    setEditingName(true);
    console.log('handleEditName: editingName set to true');
  };

  const handleSaveName = () => {
    // Prevent immediate blur from closing the edit (give 100ms grace period)
    if (Date.now() - editStartTimeRef.current < 100) {
      console.log('Ignoring immediate blur');
      return;
    }
    console.log('handleSaveName called, saving:', nameValue.trim());
    if (nameValue.trim()) {
      setManualName(campaign.campaign_id, nameValue.trim());
    }
    setEditingName(false);
  };

  const handleCancelEditName = () => {
    setEditingName(false);
    setNameValue('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission if inside a form
      handleSaveName();
    } else if (e.key === 'Escape') {
      e.preventDefault(); // Prevent any default escape behavior
      handleCancelEditName();
    }
    // Note: Tab key naturally triggers onBlur, which saves the name automatically
  };

  const handleEditNameClick = () => {
    console.log('handleEditNameClick called');
    setShowMenu(false);
    handleEditName();
    console.log('editingName should now be true');
  };

  const handlePauseClick = () => {
    setShowMenu(false);
    setShowPauseDialog(true);
  };

  const handleConfirmPause = () => {
    // Queue the pause change (no immediate API call)
    queueStatusChange({
      entityType: 'campaign',
      entityId: campaign.campaign_id,
      campaignId: campaign.campaign_id,
      action: 'pause',
      entityName: campaign.campaign_name,
      platform: 'google_ads',
    });

    // Optimistically update the UI
    if (onStatusChange) {
      onStatusChange(campaign.campaign_id, 'PAUSED');
    }

    // If this campaign has optimization enabled and was ENABLED, update allocatable budget
    // Three scenarios for continuous budget adjustment:
    //
    // 1) currentAllocatable >= totalBeforePause:
    //      new = currentAllocatable - pausedCampaignBudget
    //      (allocatable covered total, subtract paused)
    // 2) remainingBudget <= currentAllocatable < totalBeforePause:
    //      new = remainingBudget
    //      (allocatable was between, reset to remaining)
    // 3) currentAllocatable < remainingBudget:
    //      new = currentAllocatable
    //      (allocatable was very low, keep fixed)
    // Use settings.campaignStatus (store) instead of campaign.status (prop) to track pending changes
    const currentStatus = settings?.campaignStatus || campaign.status;
    if (isOptimizationEnabled && currentStatus === 'ENABLED') {
      const currentAllocatable = totalBudget;
      // Use the same `manualBudget ?? currentBudget` coalescing the
      // pool total (`useOptimizedCampaigns`) uses. Without the
      // manualBudget branch, a campaign whose budget was staged but
      // unsaved (e.g. after Apply Optimization) would be subtracted at
      // the OLD baseline, leaving the allocatable off by the delta.
      // `??` not `||` — a 0 budget is theoretically valid and would
      // fall through under `||`.
      const pausedCampaignBudget =
        settings?.manualBudget ?? settings?.currentBudget ?? campaign.budget;

      // Capture the cross-platform pool total BEFORE updating status —
      // we need `totalBeforePause` to include this campaign so the
      // scenario-branching logic sees the full pre-pause baseline.
      // `useOptimizedCampaigns().totalCurrentBudget` sums every
      // flouri-optimized + ACTIVE/ENABLED campaign across Google + Meta.
      const totalBeforePause = totalCurrentBudget;
      const remainingBudget = totalBeforePause - pausedCampaignBudget;

      let newAllocatable: number;
      if (currentAllocatable >= totalBeforePause) {
        // Scenario 1: allocatable covered or exceeded total
        newAllocatable = currentAllocatable - pausedCampaignBudget;
      } else {
        // Scenarios 2 & 3 combined: use min(currentAllocatable, remainingBudget)
        // - Scenario 2 (remainingBudget <= currentAllocatable): min() returns remainingBudget
        // - Scenario 3 (currentAllocatable < remainingBudget): min() returns currentAllocatable
        newAllocatable = Math.min(currentAllocatable, remainingBudget);
      }

      // Now update the campaign status (after we calculated totalBeforePause)
      updateCampaignStatus(campaign.campaign_id, 'PAUSED');
      setTotalBudget(newAllocatable);
    } else {
      // Campaign not optimized or not enabled, just update status
      updateCampaignStatus(campaign.campaign_id, 'PAUSED');
    }

    toast.success(`Queued: Pause "${campaign.campaign_name}". Click Save to apply.`);
    setShowPauseDialog(false);
  };

  const handleEnableClick = () => {
    setShowMenu(false);
    setShowEnableDialog(true);
  };

  const handleConfirmEnable = () => {
    // Queue the enable change (no immediate API call)
    queueStatusChange({
      entityType: 'campaign',
      entityId: campaign.campaign_id,
      campaignId: campaign.campaign_id,
      action: 'enable',
      entityName: campaign.campaign_name,
      platform: 'google_ads',
    });

    // Optimistically update the UI
    if (onStatusChange) {
      onStatusChange(campaign.campaign_id, 'ENABLED');
    }

    // Update campaign status in store first
    // Note: Unlike handleConfirmPause, we can update status before budget calculation here
    // because we're adding a fixed amount (campaignBudget) rather than calculating from getTotalCurrentBudget()
    updateCampaignStatus(campaign.campaign_id, 'ENABLED');

    // If this campaign has optimization enabled, add its budget to allocatable total
    // Use settings.campaignStatus (store) instead of campaign.status (prop) to track pending changes
    const currentStatus = settings?.campaignStatus || campaign.status;
    if (isOptimizationEnabled && currentStatus !== 'ENABLED') {
      // Symmetric to `pausedCampaignBudget` above — prefer staged
      // `manualBudget` so the allocatable increment matches what
      // `useOptimizedCampaigns` will pick up post-enable. `??` not
      // `||` to keep 0 as a valid value.
      const campaignBudget =
        settings?.manualBudget ?? settings?.currentBudget ?? campaign.budget;
      const newTotalBudgetValue = totalBudget + campaignBudget;
      setTotalBudget(newTotalBudgetValue);
    }

    toast.success(`Queued: Enable "${campaign.campaign_name}". Click Save to apply.`);
    setShowEnableDialog(false);
  };

  const handleDeleteClick = () => {
    setShowMenu(false);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    // Queue the remove change (no immediate API call)
    queueStatusChange({
      entityType: 'campaign',
      entityId: campaign.campaign_id,
      campaignId: campaign.campaign_id,
      action: 'remove',
      entityName: campaign.campaign_name,
      platform: 'google_ads',
    });

    // Optimistically remove from UI
    if (onOptimisticDelete) {
      onOptimisticDelete(campaign.campaign_id);
    }

    toast.success(`Queued: Delete "${campaign.campaign_name}". Click Save to apply.`);
    setShowDeleteDialog(false);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check both menu refs (collapsed and expanded)
      const isInsideExpandedMenu = menuRef.current && menuRef.current.contains(target);
      const isInsideCollapsedMenu = collapsedMenuRef.current && collapsedMenuRef.current.contains(target);
      if (!isInsideExpandedMenu && !isInsideCollapsedMenu) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      // Use 'click' instead of 'mousedown' to allow menu item clicks to complete
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showMenu]);

  // Card expand state - toggled by clicking the card body (all devices)
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Handle header click - navigate to the campaign page (all devices)
  const handleHeaderClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the flower button, kebab menu, or editing name
    if (
      (e.target as HTMLElement).closest('[data-flower-button]') ||
      (e.target as HTMLElement).closest('[data-kebab-menu]') ||
      editingName
    ) {
      return;
    }

    navigate(`/google-campaign/${campaign.campaign_id}`);
  };

  // Handle keyboard navigation for header (accessibility)
  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    // Skip while editing the name — otherwise preventDefault() below would
    // swallow the space key (and Enter) before the input can receive them.
    if (editingName) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/google-campaign/${campaign.campaign_id}`);
    }
  };

  // Track last toggle time to prevent rapid re-expansion on iOS
  const lastToggleTimeRef = React.useRef<number>(0);

  // Handle body click - toggle expand/collapse (all devices)
  const handleBodyClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on interactive elements or kebab menu area
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('[data-kebab-menu]')
    ) {
      return;
    }

    // Prevent rapid toggle on iOS - touch events can fire on both expanded and collapsed cards
    const now = Date.now();
    if (now - lastToggleTimeRef.current < 300) {
      return;
    }
    lastToggleTimeRef.current = now;

    // Stop propagation to prevent event from reaching underlying elements
    e.stopPropagation();

    setIsExpanded((v) => !v);
  };

  // Handle keyboard toggle for body expand/collapse (accessibility)
  const handleBodyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();

      // Use same debounce as click handler
      const now = Date.now();
      if (now - lastToggleTimeRef.current < 300) {
        return;
      }
      lastToggleTimeRef.current = now;

      setIsExpanded((v) => !v);
    }
  };

  // Card is shown expanded when manually expanded by a click, or while editing the name.
  // The kebab menu does NOT expand/collapse the card - the collapsed and expanded views
  // each render their own dropdown, so the menu works in whichever state the card is in.
  const showExpanded = isExpanded || editingName;

  // Collapse the expanded card when the user clicks anywhere outside it
  React.useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <div className="relative" ref={cardRef}>
      {/* Collapsed Card - Always Visible */}
      <div
        className={cn(
          "bg-white rounded-lg shadow-sm border transition-all duration-200",
          isOptimizationEnabled && campaign.status === 'PAUSED'
            ? "border-amber-300 bg-amber-50 shadow-md"
            : isOptimizationEnabled
            ? "border-blue-300 bg-blue-50 shadow-md"
            : "border-gray-200 hover:shadow-md"
        )}
      >
        <div className="p-6 flex flex-col">
          {/* Header with Budget Optimization Checkbox and Name */}
          <div
            className="flex items-start space-x-3 mb-4"
            onClick={handleHeaderClick}
            onKeyDown={handleHeaderKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`View campaign: ${settings?.manualName || campaign.campaign_name}`}
          >
            <div className="mt-1" data-flower-button onClick={(e) => e.stopPropagation()}>
              <FlowerButton
                size={25}
                isOpen={isOptimizationEnabled}
                onToggle={handleOptimizationToggle}
                showCenterDot={false}
              />
            </div>
            <div className="flex-1 min-w-0">
              {editingName ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="h-[1.75rem]">
                    <input
                      type="text"
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      onKeyDown={handleNameKeyDown}
                      onBlur={handleSaveName}
                      className="w-full px-2 py-0.5 text-lg font-semibold border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      aria-label="Edit campaign name"
                      aria-describedby={`campaign-name-help-${campaign.campaign_id}`}
                    />
                    <span id={`campaign-name-help-${campaign.campaign_id}`} className="sr-only">
                      Press Enter to save, Escape to cancel
                    </span>
                  </div>
                  {/* Height must match the non-editing branch (h-[1.75rem] title + h-1 notice)
                      so the collapsed card - which sets the grid cell height - does not grow
                      when editing starts, avoiding a downward shift of the rows below. */}
                  <div className="h-1" />
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    {/* Single line campaign name with ellipsis truncation */}
                    <div className="h-[1.75rem]">
                      <h3 className="text-lg font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors">
                        {settings?.manualName || campaign.campaign_name}
                      </h3>
                    </div>
                    {/* Always reserve space for modification notice */}
                    <div className="h-1">
                      {settings?.manualName && settings.manualName !== campaign.campaign_name && (
                        <div className="text-xs text-amber-600 font-medium">(name modified. Press Save to confirm.)</div>
                      )}
                    </div>
                  </div>
                  {/* Kebab menu - campaign options (all devices) */}
                  <div className="relative flex-shrink-0" ref={collapsedMenuRef} data-kebab-menu onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setShowMenu(!showMenu);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Campaign options"
                      aria-label="Campaign options menu"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="6" r="2" />
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="12" cy="18" r="2" />
                      </svg>
                    </button>
                    {showMenu && (
                      <CampaignKebabMenu
                        status={campaign.status}
                        onEditName={handleEditNameClick}
                        onPause={handlePauseClick}
                        onEnable={handleEnableClick}
                        onDelete={handleDeleteClick}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Horizontal divider */}
          <div className="border-t border-gray-200 mb-4"></div>

          {/* Collapsed View — Platform | Daily Budget | ROAS.
              Three-column grid matching MetaCampaignCard. The Campaign
              type doesn't carry `platform_source` yet (Google reads
              come straight from /campaigns, not the unified config), so
              the badge is hard-coded to "Google Ads" here. Phase C
              would route both cards through the saved config and read
              `campaign.platform_source`. */}
          <div
            className="grid grid-cols-[auto_1fr_1fr] gap-4 items-end"
            onClick={handleBodyClick}
            onKeyDown={handleBodyKeyDown}
            role="button"
            tabIndex={0}
            aria-label={isExpanded ? 'Collapse card details' : 'Expand card details'}
            aria-expanded={isExpanded}
          >
            <div className="flex items-center justify-center pb-1">
              <PlatformBadge platform="Google Ads" />
            </div>
            {/* Daily Budget */}
            <div className="relative">
              <p className="text-sm text-gray-500 mb-2">Daily Budget</p>
              <p className="text-xl font-bold text-gray-900">
                ${currentManualBudget.toFixed(2)}
              </p>
              {/* Absolutely positioned so the indicator overlays the card's
                  bottom padding instead of growing the column. With `items-end`
                  on the parent grid, an in-flow conditional indicator would
                  push the row height and shift Platform + ROAS columns down.
                  `top-full` aligns the indicator's top edge with the column's
                  bottom edge; `mt-0.5` adds a 2px breathing gap; the card's
                  p-6 padding (24px) below the grid leaves room for the text.
                  Visibility condition mirrors the BudgetBar render condition
                  in the expanded view (line 739): edit is possible when
                  optimization is OFF, OR when optimization is ON but the
                  campaign is not currently serving (e.g. PAUSED). Without
                  the status fork, a paused-but-flouri-optimized campaign
                  whose budget the user edited would silently lose the
                  staged-edit reminder. */}
              {/* Epsilon-compare matches `setManualBudget`'s drop-from-Set
                  rule (Math.abs(...) > 0.001). Strict `!==` against the
                  prop could mark a 0.0001-cent float-noise diff as a
                  staged edit even after the dirty Set cleared, leaving
                  "(Save Changes to apply)" visible with nothing pending.
                  Compare against the store baseline (post-save promoted)
                  rather than the prop, so the indicator clears as soon
                  as the save lands — not only after the React Query
                  refetch propagates the new prop. */}
              {!(isOptimizationEnabled && cardStatus === 'ENABLED') &&
                Math.abs(currentManualBudget - settingsBaselineBudget) > 0.001 && (
                <span className="absolute left-0 top-full mt-0.5 text-xs text-amber-600 font-medium whitespace-nowrap">
                  (<span className="font-bold">Save Changes</span> to apply)
                </span>
              )}
            </div>

            {/* ROAS */}
            <div>
              <p className="text-sm text-gray-500 mb-2">ROAS</p>
              <p className="text-xl font-bold text-gray-900">
                {formatRoasDisplay(roas)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Card Overlay - Appears when the card is clicked or when editing the name */}
      {showExpanded && (
        <div className="absolute top-0 left-0 right-0 z-30 animate-expand-card">
          <div className={cn(
            "bg-white rounded-lg shadow-xl border-2 overflow-hidden",
            isOptimizationEnabled && campaign.status === 'PAUSED'
              ? "border-amber-400 bg-amber-50"
              : isOptimizationEnabled
              ? "border-blue-400 bg-blue-50"
              : "border-gray-300"
          )}>
            <div className="p-6 flex flex-col animate-fade-in">
              {/* Header row - Campaign title only - clicking navigates to campaign */}
              <div
                className="flex items-start space-x-3 mb-4 cursor-pointer"
                onClick={handleHeaderClick}
                onKeyDown={handleHeaderKeyDown}
                role="button"
                tabIndex={0}
                aria-label={`View campaign: ${settings?.manualName || campaign.campaign_name}`}
              >
                <div className="mt-1" data-flower-button onClick={(e) => e.stopPropagation()}>
                  <FlowerButton
                    size={25}
                    isOpen={isOptimizationEnabled}
                    onToggle={handleOptimizationToggle}
                    showCenterDot={false}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Campaign title — heuristic two-line fork: names
                      longer than ~30 chars typically wrap at the card
                      width, so grow the slot to a full 2-line height
                      to keep breathing room above the divider. Single-
                      line names keep the original 1.75rem slot
                      untouched. */}
                  <div className={(settings?.manualName || campaign.campaign_name).length > 30 ? 'h-[3.5rem]' : 'h-[1.75rem]'}>
                    {editingName ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={nameValue}
                          onChange={(e) => setNameValue(e.target.value)}
                          onKeyDown={handleNameKeyDown}
                          onBlur={handleSaveName}
                          className="w-full px-2 py-1 text-lg font-semibold border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                          aria-label="Edit campaign name"
                          aria-describedby={`campaign-name-help-expanded-${campaign.campaign_id}`}
                        />
                        <span id={`campaign-name-help-expanded-${campaign.campaign_id}`} className="sr-only">
                          Press Enter to save, Escape to cancel
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 group w-full">
                        <h3 className="flex-1 text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                          {settings?.manualName || campaign.campaign_name}
                        </h3>
                        <div className="relative flex-shrink-0" ref={menuRef} onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setShowMenu(!showMenu);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Campaign options"
                            aria-label="Campaign options menu"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <circle cx="12" cy="6" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="12" cy="18" r="2" />
                            </svg>
                          </button>
                          {showMenu && (
                            <CampaignKebabMenu
                              status={campaign.status}
                              onEditName={handleEditNameClick}
                              onPause={handlePauseClick}
                              onEnable={handleEnableClick}
                              onDelete={handleDeleteClick}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Always reserve space for modification notice */}
                  <div className="h-1">
                    {settings?.manualName && settings.manualName !== campaign.campaign_name && (
                      <div className="text-xs text-amber-600 font-medium">(name modified. Press Save to confirm.)</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Horizontal divider - boundary between navigate and collapse zones */}
              <div className="border-t border-gray-200 mb-4"></div>

              {/* Body section - clicking collapses the card */}
              <div
                className="cursor-pointer"
                onClick={handleBodyClick}
                onKeyDown={handleBodyKeyDown}
                role="button"
                tabIndex={0}
                aria-label="Collapse card details"
                aria-expanded={true}
              >
                {/* Campaign details */}
                <div className="mb-4 pl-9">
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-semibold text-gray-700">Platform:</span> Google Ads
                  </p>
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-semibold text-gray-700">Account:</span> {formatAccountId(campaign.google_ads_account_id)}
                  </p>
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-semibold text-gray-700">Campaign Type:</span> {formatCampaignType(campaign.campaign_type) || 'Unknown'}
                  </p>
                  {/* Bidding strategy - reserved space */}
                  <div className="h-5">
                    {campaign.bidding_strategy && campaign.bidding_strategy !== 'UNKNOWN' && (
                      <p className="text-sm text-gray-500 line-clamp-1">
                        <span className="font-semibold text-gray-700">
                          {campaign.bidding_strategy.includes(',') ? 'Bidding strategies:' : 'Bidding strategy:'}
                        </span>{' '}
                        {campaign.bidding_strategy.includes(',')
                          ? campaign.bidding_strategy.split(',').map(s => formatBiddingStrategy(s.trim())).join(', ')
                          : formatBiddingStrategy(campaign.bidding_strategy)}
                      </p>
                    )}
                  </div>
                  {/* Campaign status */}
                  <div className="text-sm text-gray-500 mt-1">
                    <span className="font-semibold text-gray-700">Status:</span>{' '}
                    {pendingChange ? (
                      <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium animate-pulse ${
                        pendingChange.action === 'enable'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {pendingChange.action === 'pause' ? 'Pausing...' :
                         pendingChange.action === 'enable' ? 'Enabling...' :
                         pendingChange.action === 'remove' ? 'Removing...' : 'Pending...'}
                      </span>
                    ) : (
                      <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                        campaign.status === 'ENABLED' || campaign.status?.includes('ENABLED')
                          ? 'bg-green-100 text-green-800'
                          : campaign.status === 'PAUSED'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {formatEnumPretty(campaign.status)}
                      </span>
                    )}
                  </div>
                  {/* Budget Strategy */}
                  <div className="text-sm text-gray-500 mt-1">
                    <span className="font-semibold text-gray-700">Budget Strategy:</span>{' '}
                    <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                      isOptimizationEnabled && campaign.status === 'ENABLED'
                        ? 'bg-blue-100 text-blue-800'
                        : isOptimizationEnabled && campaign.status !== 'ENABLED'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isOptimizationEnabled && campaign.status === 'ENABLED'
                        ? '✓ Flouri Optimized'
                        : isOptimizationEnabled && campaign.status !== 'ENABLED'
                        ? 'Paused/Manual'
                        : 'Manual'}
                    </span>
                    {isOptimizationEnabled && campaign.status !== 'ENABLED' && (
                      <div className="text-xs text-amber-600 mt-0.5">
                        Enable campaign to include in optimization
                      </div>
                    )}
                  </div>
                </div>

              {/* Budget */}
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Daily Budget</p>
                  <div className="min-h-[68px]" onClick={(e) => e.stopPropagation()}>
                    {/* Show BudgetBar for manual editing when:
                        - optimization not enabled, OR
                        - optimization enabled but campaign is not ENABLED (e.g., PAUSED)
                        Use the store-backed `cardStatus` rather than the
                        prop so a queued pause/enable flips the fork
                        immediately, not after the next refetch. */}
                    {!(isOptimizationEnabled && cardStatus === 'ENABLED') ? (
                      <BudgetBar
                        value={currentManualBudget}
                        // Use the store baseline as BudgetBar's "original"
                        // so its internal modified-indicator stays in
                        // sync with this card's staged-edit check and
                        // with the post-save promoted value.
                        originalValue={settingsBaselineBudget}
                        onChange={(newValue) => setManualBudget(campaign.campaign_id, newValue)}
                        onReset={handleResetToCurrentBudget}
                        width="w-full"
                      />
                    ) : (
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          ${settingsBaselineBudget.toFixed(2)}
                        </p>
                        <p className="text-xs text-blue-600 mt-1 min-h-[20px]">
                          We will optimize this for you!
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Performance Metrics Header */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Last 28 Days</p>
                </div>

                {/* Performance Metrics */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {performanceMetrics.map((metric) => (
                    <div key={metric.label} className="h-10">
                      <p className="text-xs text-gray-500">{metric.label}</p>
                      <p className="text-sm font-medium text-gray-900">{metric.value}</p>
                    </div>
                  ))}
                  {/* Cost & Conversions */}
                  <div className="h-10">
                    <p className="text-xs text-gray-500">Total Cost</p>
                    <p className="text-sm font-medium text-gray-900">${campaign.cost.toFixed(2)}</p>
                  </div>
                  <div className="h-10">
                    <p className="text-xs text-gray-500">Conversions</p>
                    <p className="text-sm font-medium text-gray-900">{campaign.conversions}</p>
                  </div>
                </div>

                {/* ROAS */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">ROAS (Return on Ad Spend)</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatRoasDisplay(roas)}
                    </p>
                    {/* Reserved space for helper text - always takes up space */}
                    <div className="min-h-[20px] flex items-center justify-center mt-1">
                      {/* Disclaimer when using click-based ROAS.
                          Branch on `userPickedClickBased` (the user's
                          toggle choice) rather than
                          `conversionTrackingAvailable` (the platform's
                          data state) so the label reflects WHY click-
                          based is in use, not whether this particular
                          platform happens to have conversion data.
                          With the old branch, when the user pressed
                          Click Value, Google said "Click-based" and
                          Meta said "No conversion data" for the same
                          intent — breaking parity even though the
                          card code is identical. */}
                      {roas !== null && isUsingClickBasedRoas && (
                        <p className="text-xs text-amber-600">
                          {userPickedClickBased
                            ? `Click-based · $${(revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK).toFixed(2)}/click`
                            : `No conversion data · $${(revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK).toFixed(2)}/click`}
                        </p>
                      )}
                      {roas === null && !shouldUseConversionBased && campaign.clicks === 0 && (
                        <p className="text-xs text-gray-400">No clicks yet</p>
                      )}
                      {roas === null && !shouldUseConversionBased && campaign.clicks > 0 && campaign.cost === 0 && (
                        <p className="text-xs text-gray-400">No ad spend yet</p>
                      )}
                      {roas === null && shouldUseConversionBased && campaign.cost === 0 && (
                        <p className="text-xs text-gray-400">No ad spend yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enable Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showEnableDialog}
        onClose={() => setShowEnableDialog(false)}
        onConfirm={handleConfirmEnable}
        title="Enable Campaign"
        message={`Are you sure you want to enable "${settings?.manualName || campaign.campaign_name}"? This will queue the change - click Save Changes to apply.`}
        confirmText="Enable"
        cancelText="Cancel"
        variant="info"
      />

      {/* Pause Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showPauseDialog}
        onClose={() => setShowPauseDialog(false)}
        onConfirm={handleConfirmPause}
        title="Pause Campaign"
        message={`Are you sure you want to pause "${settings?.manualName || campaign.campaign_name}"? This will queue the change - click Save Changes to apply.`}
        confirmText="Pause"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Campaign"
        message={`Are you sure you want to delete "${settings?.manualName || campaign.campaign_name}"? This will queue the change - click Save Changes to apply. Once saved, this cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}